/**
 * Les trois descripteurs du parc : véhicules, remorques, chauffeurs.
 *
 * Chacun décrit ce que l'API rend (`chemin`, `cleListe`, `cleFiche`) et ce que
 * l'écran en montre. **Les champs et leurs bornes reprennent ceux de
 * `apps/api/src/moyens.ts`**, qui est la seule autorité : ce contrôle-ci
 * anticipe le sien pour afficher la faute sous le bon champ, il ne le remplace
 * pas.
 *
 * Les `court` vont par paires, comme dans l'annuaire : un `court` isolé
 * laisserait un trou à sa droite.
 */

import type { Parc } from './parc.js';

/**
 * Les véhicules moteurs.
 *
 * `type_id` PREND LA PLACE DU TEXTE LIBRE le 04/09/2026 : « Marchandise
 * Général » et « Marchandise générale » cohabitaient déjà dans la base d'essai.
 * La liste appartient à la société et se règle dans les Paramètres — un
 * transporteur frigorifique n'a pas les types de TNS.
 *
 * **Ni chauffeur ni remorque dans cette fiche** : l'attelage est une PÉRIODE,
 * dans `affectations`, et il se règle depuis l'écran Affectation. Une colonne
 * ici dirait qui conduit maintenant et laisserait « qui conduisait ce camion le
 * 12 août » sans réponse.
 */
export const PARC_VEHICULES: Parc = {
  chemin: 'parc/vehicules',
  cleListe: 'vehicules',
  cleFiche: 'vehicule_parc',
  cle: 'vehicules',
  singulier: 'véhicule',
  pluriel: 'véhicules',
  colonneNom: 'immatriculation',
  colonnes: [
    { cle: 'immatriculation', entete: 'Immatriculation', chiffre: true },
    { cle: 'type_id', entete: 'Type', liste: 'types_vehicule' },
    { cle: 'note', entete: 'Note', classe: 'col-large' },
  ],
  champs: [
    {
      cle: 'immatriculation',
      etiquette: 'Immatriculation',
      max: 32,
      obligatoire: true,
      chiffre: true,
      court: true,
    },
    {
      cle: 'type_id',
      etiquette: 'Type',
      max: 80,
      court: true,
      liste: 'types_vehicule',
    },
    { cle: 'note', etiquette: 'Note', max: 2000, long: true },
  ],
  // Le type ne se cherche plus : la colonne porte une clé, et personne ne tape
  // un identifiant dans une case de recherche.
  recherche: ['immatriculation', 'note'],
  rechercheDite: 'immatriculation, note',
  noteDesactive:
    'Il reste consultable, ainsi que les attelages et les transports déjà passés.',
};

/**
 * Les remorques.
 *
 * `vehicule_code` est un code du référentiel COMMUN des 24 gabarits (« T6 »),
 * et c'est la seule liste fermée du parc. Le contrôle de son existence
 * appartient à la base : l'écran propose, la clé étrangère refuse.
 *
 * Les deux bornes des entiers sont larges à dessein — elles ne disent pas ce
 * qu'est une remorque plausible, mais empêchent une saisie manifestement
 * fautive. Elles sont recopiées de l'API, qui les tient.
 */
export const PARC_REMORQUES: Parc = {
  chemin: 'parc/remorques',
  cleListe: 'remorques',
  cleFiche: 'remorque',
  cle: 'remorques',
  singulier: 'remorque',
  feminin: true,
  pluriel: 'remorques',
  colonneNom: 'immatriculation',
  colonnes: [
    { cle: 'immatriculation', entete: 'Immatriculation', chiffre: true },
    { cle: 'vehicule_code', entete: 'Gabarit', gabarit: true },
    {
      // LA CAPACITÉ EN MÈTRES LINÉAIRES passe AVANT les palettes : c'est elle
      // qui entre dans le calcul du remplissage depuis le 04/09/2026. Les
      // palettes restent un repère, pas une donnée de calcul.
      cle: 'capacite_ml',
      entete: 'Capacité',
      chiffre: true,
      unite: 'ML',
    },
    {
      cle: 'palettes',
      entete: 'Palettes',
      chiffre: true,
      unite: 'pal',
      // Elle part aussi en petite largeur, et c'est celle qu'on perd le moins :
      // le libellé du gabarit porte déjà une capacité (« Semi tautliner ·
      // 33 pal »). Mesuré : sans elle le tableau tient en 420 px, avec elle il
      // débordait encore de 77 px.
      classe: 'col-large',
    },
    {
      cle: 'charge_utile',
      entete: 'Charge utile',
      chiffre: true,
      unite: 'kg',
      // `col-large` : mesuré le 03/09/2026, à quatre colonnes le tableau
      // débordait de 209 px en 420 px de large. Trois colonnes tiennent, comme
      // dans l'annuaire. C'est celle-ci qui part : une charge utile se consulte
      // sur la fiche, une capacité en palettes se compare dans la liste.
      classe: 'col-large',
    },
    { cle: 'note', entete: 'Note', classe: 'col-large' },
  ],
  champs: [
    {
      cle: 'immatriculation',
      etiquette: 'Immatriculation',
      max: 32,
      obligatoire: true,
      chiffre: true,
      court: true,
    },
    {
      cle: 'vehicule_code',
      etiquette: 'Gabarit',
      max: 4,
      court: true,
      liste: 'gabarits',
    },
    {
      // CE N'EST PAS UNE MESURE AU MÈTRE RUBAN : une semi mesure 13,60 m de
      // plancher et se charge à 13,20 ML. 13,20 ÷ 0,40 = 33 palettes 80×120 et
      // 13,20 ÷ 0,50 = 26 palettes 100×120 — les deux chiffres du métier.
      // C'est le SEUL champ de cette fiche qui entre dans le remplissage.
      cle: 'capacite_ml',
      etiquette: 'Capacité en mètres linéaires',
      unite: 'ML',
      max: 6,
      maxNombre: 40,
      decimal: true,
      chiffre: true,
      // PAS `court` : la capacité prend toute la largeur de la fiche. C'est le
      // seul champ qui entre dans le calcul du remplissage, et un test de
      // `parc.test.ts` exige de toute façon que les champs courts aillent par
      // paires — il a attrapé le septième le 04/09/2026.
    },
    {
      cle: 'palettes',
      etiquette: 'Palettes',
      unite: 'pal',
      max: 3,
      maxNombre: 200,
      chiffre: true,
      court: true,
    },
    {
      cle: 'charge_utile',
      etiquette: 'Charge utile',
      unite: 'kg',
      max: 6,
      maxNombre: 100000,
      chiffre: true,
      court: true,
    },
    {
      // Les deux suivantes ne servent QU'AU DESSIN du chargement : la largeur
      // de référence du mètre linéaire est 2,40 m et ne dépend pas de la
      // remorque. Elles peuvent rester vides sans rien casser.
      cle: 'largeur_interieure',
      etiquette: 'Largeur intérieure',
      unite: 'm',
      max: 4,
      maxNombre: 5,
      decimal: true,
      chiffre: true,
      court: true,
    },
    {
      cle: 'hauteur_interieure',
      etiquette: 'Hauteur intérieure',
      unite: 'm',
      max: 4,
      maxNombre: 5,
      decimal: true,
      chiffre: true,
      court: true,
    },
    { cle: 'note', etiquette: 'Note', max: 2000, long: true },
  ],
  recherche: ['immatriculation', 'vehicule_code', 'note'],
  rechercheDite: 'immatriculation, gabarit, note',
  noteDesactive:
    'Elle reste consultable, ainsi que les attelages et les transports déjà passés.',
};

/**
 * Les chauffeurs.
 *
 * Volontairement maigres : la feuille réelle de TNS n'a que le nom. On
 * n'invente ni téléphone ni numéro de permis que personne ne remplirait — la
 * colonne viendra le jour où elle servira, et elle sera vraie ce jour-là.
 */
export const PARC_CHAUFFEURS: Parc = {
  chemin: 'chauffeurs',
  cleListe: 'chauffeurs',
  cleFiche: 'chauffeur',
  cle: 'chauffeurs',
  singulier: 'chauffeur',
  pluriel: 'chauffeurs',
  colonneNom: 'nom',
  colonnes: [
    { cle: 'nom', entete: 'Nom' },
    { cle: 'note', entete: 'Note', classe: 'col-large' },
  ],
  champs: [
    { cle: 'nom', etiquette: 'Nom', max: 200, obligatoire: true },
    { cle: 'note', etiquette: 'Note', max: 2000, long: true },
  ],
  recherche: ['nom', 'note'],
  rechercheDite: 'nom, note',
  noteDesactive:
    'Il reste consultable, ainsi que les attelages et les transports déjà passés.',
};

export const PARCS: readonly Parc[] = [PARC_VEHICULES, PARC_REMORQUES, PARC_CHAUFFEURS];
